<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateProject',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    preventDefaultOnClose: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const item = ref(null);
  const formName = ref('project');
  const formId = ref(null);
  const resource = ref(null);
  const editor = ref(null);
  const container = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    item: item.value,
    formName: formName.value,
    formId: formId.value,
    resource: resource.value,
    editor: editor.value,
    container: container.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = () => {
    return $fn.value.schemaDefaults('CreateProject');
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = () => {
    return new $fn.value.Resource(item.value);
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };
  const $$load_container = () => {
    return {
      self: $fn.value.urlFor('create_project'),
      object: 'list',
      data: [],
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'resource', dependsOn: ['item'] },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'container' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      resource: $$load_resource,
      editor: $$load_editor,
      container: $$load_container,
    },
    ux: {},
    dataRefs: {
      item,
      formName,
      formId,
      resource,
      editor,
      container,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return $fn.value.pgettext('Project', 'Project');
  });
  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Project', 'New project') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.preventDefaultOnClose,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('preventDefaultOnClose');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_elnbihwr1_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_setData_svzgj9im6_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: editor.value.getData(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_closeComponent_nhrs319en_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_navigate_ofy69aq8v_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.project',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const inst_evh_1040167445267876_created_0m8wyknfg_getWhen = async (
    $event
  ) => {
    return !props.preventDefaultOnClose;
  };
  const act_1040167445267876_reloadGrid_tqxzwcerv_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'projects',
    };
  };
  const inst_evh_1040167445267876_created_5vb7kvkae_getWhen = async (
    $event
  ) => {
    return !props.preventDefaultOnClose;
  };
  const act_1040167445267876_rest_s3uosgoh9_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_elnbihwr1 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_elnbihwr1_getActionArgs,
    });
  const inst_evh_7315092382398562_close_rf3netohr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_elnbihwr1,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_rf3netohr],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_b6b6k9pk0 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ba6zc4boz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_b6b6k9pk0,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ba6zc4boz],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_pmlaauf0s =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_e5pgy874n =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_pmlaauf0s,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_e5pgy874n],
    event: 'close',
  });
  const act_1040167445267876_setData_svzgj9im6 = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_svzgj9im6_getActionArgs,
  });
  const inst_evh_1040167445267876_before_rx0tuf6sy =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_svzgj9im6,
      event: 'before',
      displayName: 'setData',
    });
  const act_1040167445267876_closeComponent_nhrs319en =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_nhrs319en_getActionArgs,
    });
  const inst_evh_1040167445267876_created_kd444vak4 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_nhrs319en,
      event: 'created',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_ofy69aq8v =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_ofy69aq8v_getActionArgs,
    });
  const inst_evh_1040167445267876_created_0m8wyknfg =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_ofy69aq8v,
      event: 'created',
      when: inst_evh_1040167445267876_created_0m8wyknfg_getWhen,
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadGrid_tqxzwcerv =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_tqxzwcerv_getActionArgs,
    });
  const inst_evh_1040167445267876_created_5vb7kvkae =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_tqxzwcerv,
      event: 'created',
      when: inst_evh_1040167445267876_created_5vb7kvkae_getWhen,
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_rest_s3uosgoh9 = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_s3uosgoh9_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_before_rx0tuf6sy,
      inst_evh_1040167445267876_created_kd444vak4,
      inst_evh_1040167445267876_created_0m8wyknfg,
      inst_evh_1040167445267876_created_5vb7kvkae,
    ],
  });
  const inst_evh_1040167445267876_submit_va7lfabu1 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_s3uosgoh9,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_va7lfabu1],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_i12an2luc =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_8ofb8iyba =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_i12an2luc,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_8ofb8iyba],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_rf3netohr,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_ba6zc4boz,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_e5pgy874n,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_before_rx0tuf6sy,
    inst_evh_1040167445267876_created_kd444vak4,
    inst_evh_1040167445267876_created_0m8wyknfg,
    inst_evh_1040167445267876_created_5vb7kvkae,
    inst_evh_1040167445267876_submit_va7lfabu1,
    create: inst_evh_1040167445267876_submit_va7lfabu1,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_8ofb8iyba,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_rf3netohr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_rf3netohr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_ba6zc4boz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ba6zc4boz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_e5pgy874n = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_e5pgy874n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_before_rx0tuf6sy = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_before_rx0tuf6sy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_kd444vak4 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_kd444vak4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_0m8wyknfg = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_0m8wyknfg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_created_5vb7kvkae = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_created_5vb7kvkae.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_va7lfabu1 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_va7lfabu1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_8ofb8iyba = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_8ofb8iyba.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateProject"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateProject"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetProjectGeneral :project="editor"> </FieldsetProjectGeneral>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Create')"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
